import type { Company } from "@prisma/client";
import UnverifiedWarning from "~/company/components/UnverifiedWarning";
import FormFieldWrapper from "~/ui/FormFieldWrapper";
import Icon from "~/ui/Icon";
import LinkButton from "~/ui/LinkButton";
import isCompanyVerified from "~/company/helpers/isCompanyVerified";
import ROUTES from "~/kernel/routing/ROUTES";
import BookingMethod from "~/models/booking/BookingMethod";
import useTranslation from "~/kernel/internationalization/useTranslation";
import useAnalytics from "~/analytics/components/useAnalytics";

type BookButtonProps = {
  company: Company;
};

export default function BookButton({ company }: BookButtonProps) {
  const isVerified = isCompanyVerified(company);
  const { tt } = useTranslation("bookButton");
  const analytics = useAnalytics();

  switch (company.bookingMethod) {
    case BookingMethod.OnlineExisting:
    case BookingMethod.Online:
      return (
        <FormFieldWrapper>
          <LinkButton
            children={tt("bookNow")}
            disabled={!isVerified}
            to={ROUTES.newBooking.path({ params: { slug: company.slug } })}
            onClick={() =>
              analytics.track({
                type: "BEGIN_BOOKING_FROM_COMPANY_V1",
                payload: { companyId: company.id, companySlug: company.slug },
              })
            }
          />
          <UnverifiedWarning
            warningKey="customerCanNotBook"
            company={company}
          />
        </FormFieldWrapper>
      );
    case BookingMethod.Email:
      return (
        <FormFieldWrapper>
          <LinkButton
            disabled={!isVerified}
            to={`mailto:${company.contactEmail}`}
          >
            <Icon name="envelope" /> {tt("bookViaEmail")}
          </LinkButton>
          <UnverifiedWarning
            warningKey="customerCanNotBook"
            company={company}
          />
        </FormFieldWrapper>
      );
    case BookingMethod.Phone:
      return (
        <FormFieldWrapper>
          <LinkButton disabled={!isVerified} to={`tel:${company.phone}`}>
            <Icon name="phone" /> {tt("bookViaPhone")}
          </LinkButton>
          <UnverifiedWarning
            warningKey="customerCanNotBook"
            company={company}
          />
        </FormFieldWrapper>
      );
    default:
      throw new Error(
        `Booking method ${company.bookingMethod} does not exist.`,
      );
  }
}
